// Recargas Broadnet (OPERACIÓN del cajero): transacciones (para conciliar con el Excel
// del proveedor) y pago de servicios básicos. POS → Venta / Caja → Recargas (Broadnet).
// La CONFIGURACIÓN (conexión, productos, terminales, reglas, monitoreo) vive SOLO en el
// submenú POS → Broadnet (pantallas ConexionBroadnet/ProductosBroadnet/etc.).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Droplets, Loader2, Receipt, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { useAuth } from '@/lib/hooks';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { imprimirVoucherServicioBasico, type EmpresaTicket } from './print';
import { fechaLocal,
  BnConsultaResp, BnProductoFull, BnTx, Campo,
  armarExtrasBn, armarReferenciaBn, bnConsultar, bnPagarServicio, bnPagoDuplicado, bnPendientesCaja, bnProductosListar,
  bnTxEstado, bnRecargaProductoAsegurar, bnGestionDesglosar, type BnDesglose,
  bnRegularizar, bnTransacciones,
  fieldCls, money, posRpc, useTerminalCaja,
  formasPagoLocal, type FormaPago,
} from './shared';
import type { Cliente } from './PuntoVenta';
import { MarcaLogo, SelectorCatalogo, marcaDe } from './CatalogoBroadnet';
import { LOCALE } from '@/lib/utils/regional';

const hoy = () => fechaLocal();
const ESTADOS_TX = ['aprobada', 'confirmada', 'declinada', 'pendiente', 'reverso_pendiente', 'reversada', 'confirmacion_pendiente', 'fallida'];
const ESTADO_CLS: Record<string, string> = {
  aprobada: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  confirmada: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  declinada: 'bg-muted text-muted-foreground',
  reversada: 'bg-muted text-muted-foreground',
  pendiente: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  reverso_pendiente: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  confirmacion_pendiente: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  fallida: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

export function Recargas() {
  const [tab, setTab] = useState<'tx' | 'servicios'>('tx');
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Smartphone className="w-5 h-5" /> Recargas Broadnet</h1>
      <div className="flex items-center gap-2 border-b">
        {([['tx', 'Transacciones'], ['servicios', 'Servicios básicos']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === 'tx' ? <Transacciones /> : <ServiciosBasicos />}
    </div>
  );
}

function Transacciones() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());
  const [estado, setEstado] = useState('');

  // Datos de la empresa para el encabezado del recibo reimpreso
  const { data: empresa } = useQuery<EmpresaTicket>({
    queryKey: ['pos-empresa-ticket', user?.empresa?.id],
    enabled: !!user?.empresa?.id,
    queryFn: async () => {
      const rows = (await apiClient.get<EmpresaTicket[]>(`/rest/autenticacion.empresas?id=eq.${user!.empresa.id}&select=nombre,ruc,direccion,telefono,logo,moneda:moneda_funcional`)).data;
      return rows[0] ?? ({ nombre: user?.empresa?.nombre, logo: user?.empresa?.logo } as EmpresaTicket);
    },
  });

  // Reimpresión del recibo de un SERVICIO BÁSICO aprobado (704): bn_tx_estado
  // trae el tiquete OFICIAL guardado (703) y los datos del voucher.
  const [reimprimiendo, setReimprimiendo] = useState<string | null>(null);
  const reimprimir = async (t: BnTx) => {
    setReimprimiendo(t.id);
    try {
      const est = await bnTxEstado(t.id);
      imprimirVoucherServicioBasico({
        proveedor: est.proveedor, servicio: est.servicio, referencia: est.celular,
        titular: null, monto: Number(est.monto), autorizacion: est.autorizacion,
        notaVenta: est.nota_venta, tiquete: est.tiquete ?? null,
        fecha: new Date(t.fecha).toLocaleString(LOCALE), caja: t.caja, cajero: t.cajero,
      }, empresa ?? ({ nombre: user?.empresa?.nombre, logo: user?.empresa?.logo } as EmpresaTicket));
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setReimprimiendo(null); }
  };

  const { data: txs = [], isFetching } = useQuery<BnTx[]>({
    queryKey: ['pos-bn-tx', desde, hasta, estado],
    queryFn: () => bnTransacciones(desde || null, hasta || null, null, estado || null),
    staleTime: 0, refetchOnMount: 'always',
  });
  const regularizar = useMutation({
    mutationFn: (id: string) => bnRegularizar(id),
    onSuccess: (r: { estado: string; ya_resuelta?: boolean }) =>
      (r.ya_resuelta ? toast.info(`Ya se había resuelto sola: ${r.estado}`) : toast.success(`Regularizada: ${r.estado}`)),
    onError: (e) => {
      const msg = getErrorMessage(e);
      // Ya resuelta (worker/otra caja/click previo): la lista estaba desactualizada.
      if (/no está pendiente/i.test(msg)) toast.info('Esa transacción ya estaba regularizada — actualizo la lista');
      else toast.error(msg);
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['pos-bn-tx'] }); },
  });

  const vendidas = useMemo(() => txs.filter((t) => t.estado === 'aprobada' || t.estado === 'confirmada'), [txs]);
  // Las tres cifras no se suman entre sí: el monto del servicio es lo que se
  // concilia con Broadnet, y la ganancia son las dos comisiones menos el IVA
  // de la de gestión —ese IVA no es del comercio, es del SRI—.
  const tot = useMemo(() => vendidas.reduce((a, t) => ({
    monto: a.monto + Number(t.monto || 0),
    comisionBn: a.comisionBn + Number(t.comision_broadnet || 0),
    gestion: a.gestion + Number(t.gestion || 0),
    cobrado: a.cobrado + (Number(t.monto_cobrado) || Number(t.monto || 0)),
    ganancia: a.ganancia + Number(t.ganancia || 0),
  }), { monto: 0, comisionBn: 0, gestion: 0, cobrado: 0, ganancia: 0 }), [vendidas]);
  const exportFilas = useMemo<Celda[][]>(() => {
    const filas: Celda[][] = txs.map((t, i) => [i + 1, new Date(t.fecha).toLocaleString(LOCALE), t.lote, t.referencia,
      t.caja, t.cajero, t.proveedor, t.celular,
      Number(t.monto), Number(t.comision_broadnet || 0), Number(t.gestion || 0),
      Number(t.monto_cobrado) || Number(t.monto),
      t.estado, t.autorizacion, t.nota_venta?.trim() ?? null]);
    if (filas.length) filas.push(['', 'Totales (vendidas)', '', '', '', '', '', '',
      tot.monto, tot.comisionBn, tot.gestion, tot.cobrado, '', '', '']);
    return filas;
  }, [txs, tot]);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <Campo label="Desde"><input type="date" className={`${fieldCls} h-9`} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta"><input type="date" className={`${fieldCls} h-9`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          <Campo label="Estado">
            <select className={`${fieldCls} h-9 w-52`} value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="">Todos</option>
              {ESTADOS_TX.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </Campo>
          <div className="ml-auto">
            <ExportarBotones nombre="recargas_broadnet" titulo="Recargas Broadnet"
              subtitulo={`${desde} a ${hasta} — cruzar por REFERENCIA y AUTORIZACIÓN vs conciliación Broadnet`}
              headers={['N°', 'Fecha', 'Lote', 'Referencia', 'Caja', 'Cajero', 'Proveedor', 'Celular',
                        'Monto servicio', 'Com. Broadnet', 'Com. gestión', 'Cobrado',
                        'Estado', 'Autorización', 'Nota venta']}
              filas={exportFilas} colsDerecha={[0, 8, 9, 10, 11]} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Transacciones ({txs.length}) · servicio {money(tot.monto)} · cobrado {money(tot.cobrado)}
          {tot.ganancia > 0 && (
            <span className="ml-2 font-normal text-emerald-700 dark:text-emerald-400">
              ganancia {money(tot.ganancia)}
            </span>)}
          {isFetching && <Loader2 className="inline w-4 h-4 animate-spin ml-1" />}
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Ref.</th>
              <th className="py-2 pr-3">Caja / Cajero</th><th className="py-2 pr-3">Proveedor</th><th className="py-2 pr-3">Celular</th>
              <th className="py-2 pr-3 text-right">Monto servicio</th>
              <th className="py-2 pr-3 text-right">Com. Broadnet</th>
              <th className="py-2 pr-3 text-right">Com. gestión</th>
              <th className="py-2 pr-3 text-right">Cobrado</th>
              <th className="py-2 pr-3">Estado</th>
              <th className="py-2 pr-3">Autorización</th><th className="py-2 pr-3">Nota venta</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {txs.length === 0 && <tr><td colSpan={14} className="py-8 text-center text-muted-foreground">Sin transacciones en el rango</td></tr>}
              {txs.map((t, i) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-xs">{new Date(t.fecha).toLocaleString(LOCALE)}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{t.referencia}</td>
                  <td className="py-2 pr-3 text-xs">{t.caja}<div className="text-muted-foreground">{t.cajero ?? ''}</div></td>
                  <td className="py-2 pr-3">{t.proveedor}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{t.celular}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(t.monto)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-muted-foreground">
                    {Number(t.comision_broadnet) > 0 ? money(Number(t.comision_broadnet)) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-emerald-700 dark:text-emerald-400">
                    {Number(t.gestion) > 0 ? money(Number(t.gestion)) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums font-medium">
                    {money(Number(t.monto_cobrado) || Number(t.monto))}
                  </td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${ESTADO_CLS[t.estado] ?? ''}`} title={t.mensaje ?? ''}>{t.estado.replace('_', ' ')}</span>
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs">{t.autorizacion ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{t.nota_venta?.trim() || '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {['pendiente', 'reverso_pendiente', 'confirmacion_pendiente'].includes(t.estado) && (
                      <Button size="sm" variant="outline" isLoading={regularizar.isPending} onClick={() => regularizar.mutate(t.id)}>Regularizar</Button>
                    )}
                    {t.tipo === 'servicio_basico' && ['aprobada', 'confirmada'].includes(t.estado) && (
                      <Button size="sm" variant="outline" isLoading={reimprimiendo === t.id}
                        title="Reimprimir el recibo del proveedor" onClick={() => void reimprimir(t)}>
                        <Receipt className="w-3.5 h-3.5 mr-1" /> Reimprimir
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {txs.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={6}>Totales — vendidas {vendidas.length} de {txs.length}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot.monto)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot.comisionBn)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-emerald-700 dark:text-emerald-400">{money(tot.gestion)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot.cobrado)}</td>
                  <td colSpan={4} />
                </tr>
              )}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-muted-foreground">
            Concilia contra el Excel diario de Broadnet cruzando <span className="font-medium">Referencia + Autorización</span> (exporta esta tabla)
            y usando la columna <span className="font-medium">Monto servicio</span>, no «Cobrado»: el proveedor no ve tu comisión de gestión.
            Recuerda: tienes <span className="font-medium">48 horas</span> para reportar inconsistencias al proveedor.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Servicios básicos (Fase 3): consulta de deuda + pago, solo efectivo.
// ---------------------------------------------------------------------------

// Gate común: resuelve caja del equipo y sesión abierta (mismo patrón de Caja.tsx).
function useCajaSesion() {
  const { caja, loading } = useTerminalCaja();
  const sesionQ = useQuery({
    queryKey: ['pos-sesion', caja?.id], enabled: !!caja,
    queryFn: () => posRpc<{ id: string; codigo: string } | null>('pos_sesion_activa', { p_caja_id: caja!.id }),
    staleTime: 0, refetchOnMount: 'always',
  });
  return { caja, loading, sesion: sesionQ.data ?? null, sesionLoading: sesionQ.isFetching };
}

// Exportado: también se monta como pestaña del modal Recargas (F4) del punto de
// venta. Es autocontenido (resuelve caja/sesión del equipo con useTerminalCaja).
// `visual` (911): en el F4 del POS el servicio se elige por marca con tarjetas,
// no con el desplegable. La pantalla POS → Recargas sigue igual.
export function ServiciosBasicos({ visual = false }: { visual?: boolean } = {}) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { user } = useAuth();
  const { caja, loading, sesion, sesionLoading } = useCajaSesion();

  const { data: empresa } = useQuery<EmpresaTicket>({
    queryKey: ['pos-empresa-ticket', user?.empresa?.id],
    enabled: !!user?.empresa?.id,
    queryFn: async () => {
      const rows = (await apiClient.get<EmpresaTicket[]>(`/rest/autenticacion.empresas?id=eq.${user!.empresa.id}&select=nombre,ruc,direccion,telefono,logo,moneda:moneda_funcional`)).data;
      return rows[0] ?? ({ nombre: user?.empresa?.nombre, logo: user?.empresa?.logo } as EmpresaTicket);
    },
  });
  const empresaTicket: EmpresaTicket = empresa ?? ({ nombre: user?.empresa?.nombre, logo: user?.empresa?.logo } as EmpresaTicket);
  const moneda = empresaTicket.moneda ?? 'USD';
  const cajeroNombre = user?.username ?? null;

  const { data: servicios = [] } = useQuery<BnProductoFull[]>({
    queryKey: ['pos-bn-servicios'], queryFn: () => bnProductosListar('servicio_basico'), staleTime: 5 * 60 * 1000,
  });
  const activos = useMemo(() => servicios.filter((s) => s.es_activo), [servicios]);
  const porProveedor = useMemo(() => {
    const m = new Map<string, BnProductoFull[]>();
    for (const s of activos) { const arr = m.get(s.proveedor_nombre) ?? []; arr.push(s); m.set(s.proveedor_nombre, arr); }
    return [...m.entries()];
  }, [activos]);

  const { data: pendientes = [] } = useQuery<{ id: string; estado: string }[]>({
    queryKey: ['pos-bn-pendientes', caja?.id], enabled: !!caja,
    queryFn: () => bnPendientesCaja(caja!.id), staleTime: 0, refetchOnMount: 'always',
    // El worker resuelve las pendientes en segundos: mientras haya alguna,
    // re-consultar cada 4 s para que el candado se libere solo (702).
    refetchInterval: (q) => (((q.state.data as unknown[] | undefined)?.length ?? 0) > 0 ? 4000 : false),
  });
  const bloqueada = pendientes.length > 0;

  const [productoId, setProductoId] = useState('');
  const [valores, setValores] = useState<Record<string, string>>({});
  const [montoManual, setMontoManual] = useState('');
  const [titularManual, setTitularManual] = useState('');
  const [consulta, setConsulta] = useState<BnConsultaResp | null>(null);

  const producto = activos.find((s) => s.id === productoId);
  const campos = useMemo(() => producto?.campos_requeridos ?? [], [producto]);
  const referencia = useMemo(() => armarReferenciaBn(campos, valores), [campos, valores]);
  const camposCompletos = campos.length > 0 && campos.every((c) => !c.requerido || (valores[c.clave] ?? '').trim().length > 0);

  const resetFormulario = () => { setProductoId(''); setValores({}); setMontoManual(''); setTitularManual(''); setConsulta(null); };
  const cambiarProducto = (id: string) => { setProductoId(id); setValores({}); setMontoManual(''); setTitularManual(''); setConsulta(null); };

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['pos-bn-pendientes', caja?.id] });
    void qc.invalidateQueries({ queryKey: ['pos-bn-tx'] });
  };

  const consultar = useMutation({
    mutationFn: () => bnConsultar(caja!.id, productoId, referencia, armarExtrasBn(campos, valores)),
    onSuccess: (r) => {
      if (r.ok) setConsulta(r);
      else { setConsulta(null); toast.error(r.mensaje || 'No se pudo consultar la deuda'); }
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Monto/titular efectivos según haya (o no) consulta de deuda.
  const montoModo = producto?.monto_modo ?? 'abierto';
  const montoAPagar = producto?.soporta_consulta
    ? (consulta?.monto ?? 0)
    : montoModo === 'fijo' ? (producto?.monto_fijo ?? 0) : (Number(montoManual) || 0);
  const titularAPagar = consulta?.titular ?? (titularManual.trim() || null);
  const tranRec = consulta?.tran_rec ?? null;

  // Desglose en vivo: qué se paga al proveedor y qué se le cobra al cliente.
  // En los recibos la comisión SIEMPRE se suma (837): al valor de la empresa
  // eléctrica no se le puede restar nada.
  const { data: desglose } = useQuery<BnDesglose>({
    queryKey: ['pos-bn-desglose-sb', productoId, montoAPagar],
    queryFn: () => bnGestionDesglosar(productoId, montoAPagar),
    enabled: !!productoId && montoAPagar > 0,
    staleTime: 60 * 1000,
  });

  const montoManualValido =
    montoModo === 'fijo' ? (producto?.monto_fijo ?? 0) > 0
    : montoModo === 'rango' ? (Number(montoManual) || 0) >= (producto?.monto_min ?? 0) && (Number(montoManual) || 0) <= (producto?.monto_max ?? Infinity)
    : (Number(montoManual) || 0) > 0;

  const listoParaCobrar = !!producto && camposCompletos && !bloqueada
    && (producto.soporta_consulta ? (!!consulta?.ok && montoAPagar > 0) : montoManualValido);

  // 814 · Un servicio básico es, por defecto, una RECAUDACIÓN: el comercio no
  // vende nada y sólo entrega el recibo. Pero si en Productos se marcó ese
  // servicio como «lo factura el comercio» —hay acuerdos así con catálogos—,
  // además del recibo se emite la factura SRI, igual que con las recargas.
  const facturaComercio = producto?.facturacion_efectiva === 'comercio';
  const { data: consumidorFinal } = useQuery<Cliente>({
    queryKey: ['pos-cf'], queryFn: () => posRpc<Cliente>('pos_consumidor_final', {}),
    staleTime: 30 * 60 * 1000, enabled: facturaComercio,
  });
  const { data: formasLocal = [] } = useQuery<FormaPago[]>({
    queryKey: ['pos-formas-local-sb', caja?.location_id ?? ''],
    queryFn: () => formasPagoLocal(caja!.location_id), enabled: facturaComercio && !!caja,
    staleTime: 5 * 60 * 1000,
  });

  const facturarServicio = useMutation({
    mutationFn: async (r: { monto: number; proveedor: string; referencia: string }) => {
      if (!consumidorFinal) throw new Error('Sin cliente resuelto (Consumidor Final) — no se puede facturar');
      const efectivo = formasLocal.find((f) => f.tipo === 'efectivo');
      if (!efectivo) throw new Error('El local no tiene una forma de pago EFECTIVO habilitada');
      const prodInvId = await bnRecargaProductoAsegurar(productoId);
      return posRpc('pos_registrar_venta', {
        payload: {
          caja_id: caja!.id, cliente_partner_id: consumidorFinal.id, tipo_doc: 'factura',
          observaciones: null, precio_fijo: true,
          lineas: [{
            producto_id: prodInvId, cantidad: 1, precio_unitario: r.monto, descuento: 0,
            iva_tarifa: 0, iva_monto: 0,
            descripcion: `SERVICIO ${r.proveedor} ${r.referencia}`,
          }],
          pagos: [{ forma_pago_id: efectivo.id, monto: r.monto, referencia: null }],
        },
      });
    },
    onSuccess: () => toast.success('Factura emitida por el servicio'),
    // El cobro YA se hizo en Broadnet: si la factura falla, no se deshace nada.
    // Se avisa fuerte para que se emita a mano, que es lo único correcto.
    onError: (e) => toast.error(`El servicio se cobró pero la FACTURA falló: ${getErrorMessage(e)} — emítela a mano`),
  });

  const imprimir = (r: { proveedor: string; monto: number; autorizacion: string | null; nota_venta: string | null; referencia: string; titular?: string | null; tiquete?: string | null; gestion?: number; monto_cobrado?: number }) =>
    imprimirVoucherServicioBasico({
      proveedor: r.proveedor, servicio: producto?.servicio_nombre ?? null, referencia: r.referencia,
      titular: r.titular ?? titularAPagar, monto: Number(r.monto), autorizacion: r.autorizacion, notaVenta: r.nota_venta,
      caja: caja?.nombre, cajero: cajeroNombre,
      tiquete: r.tiquete ?? null,   // recibo OFICIAL del proveedor (703): manda sobre el voucher propio
      // 832: si hubo comisión de gestión, el cliente pagó MÁS que el recibo del
      // proveedor. Sin esto en el papel, el reclamo es inmediato.
      gestion: Number(r.gestion) || 0,
      totalCobrado: Number(r.monto_cobrado) || undefined,
    }, empresaTicket);

  // Anti-doble-pago (701): si HOY ya hay un pago aprobado del mismo servicio y
  // la misma cuenta, pedir confirmación explícita antes de cobrar de nuevo.
  const confirmarNoDuplicado = async (): Promise<boolean> => {
    try {
      const previo = await bnPagoDuplicado(productoId, referencia);
      if (!previo) return true;
      const hora = new Date(previo.fecha).toLocaleTimeString(LOCALE, { hour12: false });
      return await dlg.confirmar('¿Cobrar OTRA VEZ la misma cuenta?', {
        detalle: `⚠ Esta cuenta YA tiene un pago APROBADO HOY:\n\n${money(Number(previo.monto))} a las ${hora}` +
          `${previo.autorizacion ? ` · aut. ${previo.autorizacion}` : ''}${previo.caja ? ` · ${previo.caja}` : ''}`,
        peligro: true,
      });
    } catch { return true; /* si la verificación falla, no bloquear el cobro */ }
  };

  const pagar = useMutation({
    mutationFn: async () => {
      if (!(await confirmarNoDuplicado())) throw new Error('Cobro cancelado (pago duplicado)');
      return bnPagarServicio(caja!.id, productoId, referencia, montoAPagar, titularAPagar, tranRec, armarExtrasBn(campos, valores));
    },
    onSuccess: (r) => {
      invalidar();
      if (r.estado === 'aprobada' || r.estado === 'confirmada') {
        toast.success(`Pago aprobado · aut. ${r.autorizacion}`);
        imprimir(r);
        if (facturaComercio) facturarServicio.mutate({ monto: Number(r.monto), proveedor: r.proveedor, referencia: r.referencia });
        resetFormulario();
      }
      else if (r.estado === 'declinada') toast.error(`Declinada: ${r.mensaje ?? ''}`);
      else toast.warning(`Transacción ${r.estado.replace('_', ' ')} — regularízala en Transacciones antes de pagar otra.`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const regularizar = useMutation({
    mutationFn: (id: string) => bnRegularizar(id),
    onSuccess: (r: { estado: string; ya_resuelta?: boolean }) => {
      if (r.ya_resuelta) toast.info(`Ya se había resuelto sola: ${r.estado} — lista actualizada`);
      else toast.success(`Regularizada: ${r.estado}`);
      invalidar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (loading || sesionLoading) return <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto w-6 h-6 animate-spin" /></div>;
  if (!caja) return <AvisoCaja texto="Este equipo no está registrado a una caja. Abre POS → Punto de venta para registrarlo." />;
  if (!sesion) return <AvisoCaja texto={`No hay un turno abierto en ${caja.nombre}. Abre la caja en POS → Apertura antes de cobrar servicios básicos.`} />;

  return (
    <div className="space-y-4">
      {dlg.ui}
      {bloqueada && (
        <Card className="border-red-300 dark:border-red-900/40">
          <CardContent className="pt-4 text-sm">
            <p className="font-medium text-red-700 dark:text-red-300 mb-2">
              Hay {pendientes.length} transacción(es) Broadnet pendiente(s) de regularizar en esta caja — no se puede cobrar otra (norma Broadnet).
            </p>
            {pendientes.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-0.5">
                <span className="text-xs capitalize">{t.estado.replace('_', ' ')}</span>
                <Button size="sm" variant="outline" isLoading={regularizar.isPending} onClick={() => regularizar.mutate(t.id)}>Regularizar</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Droplets className="w-4 h-4" /> Pago de servicio básico · {caja.nombre}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {visual ? (
            !producto
              ? <SelectorCatalogo productos={activos} onElegir={(p) => cambiarProducto(p.id)} />
              : (
                <div className="flex items-center gap-3 rounded-xl border p-3" style={{ borderColor: marcaDe(producto).color }}>
                  <MarcaLogo marca={marcaDe(producto)} size={40} />
                  <span className="flex-1 font-semibold">{marcaDe(producto).nombre} · {producto.servicio_nombre.replace(/_/g, ' ')}</span>
                  <Button size="sm" variant="outline" onClick={() => cambiarProducto('')}>Cambiar</Button>
                </div>
              )
          ) : (
          <Campo label="Servicio *">
            <select className={fieldCls} value={productoId} onChange={(e) => cambiarProducto(e.target.value)}>
              <option value="">— Selecciona —</option>
              {porProveedor.map(([prov, lista]) => (
                <optgroup key={prov} label={prov}>
                  {lista.map((s) => <option key={s.id} value={s.id}>{s.servicio_nombre}</option>)}
                </optgroup>
              ))}
            </select>
          </Campo>
          )}
          {!activos.length && <p className="text-xs text-muted-foreground">No hay servicios básicos activos — configúralos en la pestaña Configuración.</p>}

          {producto && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {campos.map((c) => (
                  <Campo key={c.clave} label={`${c.label}${c.requerido ? ' *' : ''}`}>
                    {c.tipo === 'combo' ? (
                      <select className={fieldCls} value={valores[c.clave] ?? ''}
                              onChange={(e) => { setValores((v) => ({ ...v, [c.clave]: e.target.value })); setConsulta(null); }}>
                        <option value="">— Selecciona —</option>
                        {(c.opciones ?? []).map((o) => <option key={o.valor} value={o.valor}>{o.valor} · {o.label}</option>)}
                      </select>
                    ) : (
                      <input
                        className={fieldCls}
                        inputMode={c.tipo === 'texto' ? 'text' : 'numeric'}
                        value={valores[c.clave] ?? ''}
                        onChange={(e) => { setValores((v) => ({ ...v, [c.clave]: e.target.value })); setConsulta(null); }}
                        placeholder={c.tipo === 'telefono' ? '09XXXXXXXX' : c.tipo === 'cedula' ? 'Cédula/RUC' : ''}
                      />
                    )}
                  </Campo>
                ))}
              </div>

              {producto.soporta_consulta ? (
                <div className="flex items-center gap-3">
                  <Button variant="outline" isLoading={consultar.isPending} disabled={!camposCompletos || bloqueada} onClick={() => consultar.mutate()}>
                    Consultar deuda
                  </Button>
                  {consulta?.simulado && <span className="text-xs text-muted-foreground">(modo simulado)</span>}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Campo label={montoModo === 'fijo' ? 'Monto (fijo)' : montoModo === 'rango' ? `Monto (entre ${money(producto.monto_min ?? 0, moneda)} y ${money(producto.monto_max ?? 0, moneda)})` : 'Monto *'}>
                    {montoModo === 'fijo'
                      ? <input className={fieldCls} value={money(producto.monto_fijo ?? 0, moneda)} disabled />
                      : <input type="number" min={montoModo === 'rango' ? (producto.monto_min ?? 0) : 0} max={montoModo === 'rango' ? (producto.monto_max ?? undefined) : undefined}
                          step="0.01" className={`${fieldCls} text-right`} value={montoManual} onChange={(e) => setMontoManual(e.target.value)} />}
                  </Campo>
                  <Campo label="Titular (opcional)" className="col-span-2">
                    <input className={fieldCls} value={titularManual} onChange={(e) => setTitularManual(e.target.value)} />
                  </Campo>
                </div>
              )}

              {consulta?.ok && (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm dark:border-emerald-900/40 dark:bg-emerald-900/15">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Titular</span>
                    <span className="font-medium">{consulta.titular ?? '—'}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-muted-foreground">Monto a pagar</span>
                    <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{money(consulta.monto ?? 0, moneda)}</span>
                  </div>
                  {consulta.publicidad && <p className="mt-2 text-xs text-muted-foreground">{consulta.publicidad}</p>}
                </div>
              )}

              {desglose && Number(desglose.comision) > 0 && (
                <div className="rounded-md border border-primary/40 bg-primary/5 p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Valor del servicio</span>
                    <span className="tabular-nums">{money(desglose.recarga, moneda)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Comisión de gestión</span>
                    <span className="tabular-nums">{money(desglose.comision, moneda)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between border-t pt-1 font-semibold">
                    <span>Total a cobrar</span>
                    <span className="tabular-nums">{money(desglose.total, moneda)}</span>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Cobro <span className="font-medium">solo en EFECTIVO</span>: recibe{' '}
                {money(desglose && Number(desglose.comision) > 0 ? desglose.total : montoAPagar, moneda)}{' '}
                antes de confirmar.
              </p>
              <div className="flex justify-end gap-2 border-t pt-3">
                <Button variant="outline" onClick={resetFormulario}>Limpiar</Button>
                <Button isLoading={pagar.isPending} disabled={!listoParaCobrar} onClick={() => pagar.mutate()}>
                  <Receipt className="w-4 h-4 mr-1" /> Cobrar
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AvisoCaja({ texto }: { texto: string }) {
  return <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">{texto}</div>;
}
